import React, { useState, useEffect } from "react";
import { Loader2, Wallet, AlertTriangle } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
} from "recharts";
import { api } from "@/lib/api";
import SectionHeader from "@/components/SectionHeader";
import { useActiveProject } from "@/lib/ActiveProjectContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const tooltipStyle = {
  contentStyle: {
    background: "rgba(15,23,42,0.95)",
    border: "1px solid #1E293B",
    borderRadius: 2,
    color: "#F8FAFC",
    fontSize: 12,
    fontFamily: "IBM Plex Mono, monospace",
  },
  labelStyle: { color: "#94A3B8" },
};

export default function FinanceAI() {
  const { project } = useActiveProject();
  const [name, setName] = useState("");
  const [budget, setBudget] = useState(612);
  const [spent, setSpent] = useState(458);
  const [notes, setNotes] = useState(
    "PV module CIF cost up 7% due to Suez surcharge; mounting structures procured 17% above estimate; substation equipment savings ~5%."
  );
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  // Prefill from active project
  useEffect(() => {
    if (!project) return;
    setName(project.name || "");
    if (project.budget_usd_mn != null) setBudget(project.budget_usd_mn);
    if (project.spent_usd_mn != null) setSpent(project.spent_usd_mn);
    if (project.notes) setNotes(project.notes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/finance/analyze", {
        project_name: name,
        budget: Number(budget),
        spent: Number(spent),
        notes,
      });
      setData(data);
      toast.success("Finance analysis complete");
    } catch (e) {
      toast.error("Analysis failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="finance-ai-page">
      <SectionHeader
        overline="Module 03"
        title="Finance AI"
        description="Cost variance, cashflow forecasting and overrun signal detection. MIS-grade output."
        testid="finance-ai-header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-4 bg-slate-900/60 border border-slate-800 rounded-sm p-4 space-y-3 h-fit">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-cyan-400">
            INPUT
          </div>
          <div>
            <Label className="text-xs text-slate-400">Project</Label>
            <Input
              data-testid="finance-project"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1.5 bg-slate-950 border-slate-800 rounded-sm text-slate-100"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-slate-400">Budget ($mn)</Label>
              <Input
                data-testid="finance-budget"
                type="number"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                className="mt-1.5 bg-slate-950 border-slate-800 rounded-sm text-slate-100"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-400">Spent ($mn)</Label>
              <Input
                data-testid="finance-spent"
                type="number"
                value={spent}
                onChange={(e) => setSpent(e.target.value)}
                className="mt-1.5 bg-slate-950 border-slate-800 rounded-sm text-slate-100"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-400">Cost notes</Label>
            <Textarea
              data-testid="finance-notes"
              rows={5}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mt-1.5 bg-slate-950 border-slate-800 rounded-sm text-slate-100"
            />
          </div>
          <Button
            onClick={run}
            disabled={loading}
            data-testid="finance-run-btn"
            className="w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold rounded-sm"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Building MIS…
              </>
            ) : (
              "Generate MIS Report"
            )}
          </Button>
        </div>

        <div className="lg:col-span-8 space-y-4">
          {!data && !loading && (
            <div
              className="bg-slate-900/40 border border-dashed border-slate-800 rounded-sm p-12 text-center"
              data-testid="finance-empty"
            >
              <Wallet className="h-10 w-10 text-slate-700 mx-auto mb-4" />
              <h3 className="font-display text-lg text-slate-300">
                MIS report awaiting input
              </h3>
              <p className="text-sm text-slate-500 mt-1.5">
                Generate variance, 3-month forecast, savings and risk alerts.
              </p>
            </div>
          )}
          {loading && (
            <div className="bg-slate-900/60 border border-slate-800 rounded-sm p-10 text-center">
              <Loader2 className="h-8 w-8 text-cyan-400 animate-spin mx-auto mb-3" />
              <div className="font-mono text-xs text-cyan-400 uppercase tracking-[0.2em]">
                Crunching cashflow…
              </div>
            </div>
          )}
          {data && <FinanceResults data={data} />}
        </div>
      </div>
    </div>
  );
}

function FinanceResults({ data }) {
  return (
    <div className="space-y-4" data-testid="finance-results">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Stat
          label="Variance"
          value={`${(data.variance_pct ?? 0).toFixed(1)}`}
          unit="%"
          danger={(data.variance_pct ?? 0) > 0}
        />
        <Stat label="Overrun risks" value={(data.overrun_risks || []).length} />
        <Stat label="Savings ideas" value={(data.savings_opportunities || []).length} />
      </div>

      {data.cashflow_forecast?.length ? (
        <Panel title="3-Month Cashflow Forecast">
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={data.cashflow_forecast}>
              <CartesianGrid stroke="#1E293B" strokeDasharray="2 4" />
              <XAxis
                dataKey="month"
                stroke="#475569"
                tick={{ fill: "#94A3B8", fontSize: 11 }}
              />
              <YAxis
                stroke="#475569"
                tick={{ fill: "#94A3B8", fontSize: 11 }}
              />
              <Tooltip {...tooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 12, color: "#94A3B8" }} />
              <Bar dataKey="inflow" fill="#06B6D4" />
              <Bar dataKey="outflow" fill="#3B82F6" />
              <Line
                type="monotone"
                dataKey="net"
                stroke="#F59E0B"
                strokeWidth={2}
                dot={{ r: 3 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </Panel>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Overrun Risks">
          <ul className="space-y-2 text-sm text-slate-200">
            {(data.overrun_risks || []).map((r, i) => (
              <li
                key={i}
                className="flex gap-2 border border-slate-800 bg-slate-950/50 px-3 py-2 rounded-sm"
              >
                <AlertTriangle className="h-3.5 w-3.5 text-rose-400 mt-0.5 shrink-0" />
                {r}
              </li>
            ))}
          </ul>
        </Panel>
        <Panel title="Savings Opportunities">
          <ul className="space-y-2 text-sm text-slate-200">
            {(data.savings_opportunities || []).map((r, i) => (
              <li
                key={i}
                className="flex gap-2 border border-slate-800 bg-slate-950/50 px-3 py-2 rounded-sm"
              >
                <span className="text-emerald-400 mt-1 font-mono text-xs">+</span>
                {r}
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      {data.alerts?.length ? (
        <Panel title="Financial Alerts">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-800 hover:bg-transparent">
                <TableHead className="text-slate-500 text-[10px] font-mono uppercase">
                  Level
                </TableHead>
                <TableHead className="text-slate-500 text-[10px] font-mono uppercase">
                  Title
                </TableHead>
                <TableHead className="text-slate-500 text-[10px] font-mono uppercase">
                  Detail
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.alerts.map((a, i) => {
                const v =
                  a.level === "danger"
                    ? "bg-rose-500/15 text-rose-300 border-rose-700"
                    : a.level === "warning"
                    ? "bg-amber-500/15 text-amber-300 border-amber-700"
                    : "bg-cyan-500/15 text-cyan-300 border-cyan-700";
                return (
                  <TableRow
                    key={i}
                    className="border-slate-800 hover:bg-slate-900/50"
                  >
                    <TableCell>
                      <Badge className={`rounded-sm border ${v}`}>
                        {a.level}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-100 text-sm">
                      {a.title}
                    </TableCell>
                    <TableCell className="text-slate-400 text-sm">
                      {a.detail}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Panel>
      ) : null}

      {data.summary && (
        <div className="bg-slate-900/80 border-l-2 border-cyan-500 border-y border-r border-slate-800 rounded-sm p-4 text-sm text-slate-200">
          {data.summary}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, unit, danger }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-sm p-4">
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">
        {label}
      </div>
      <div className="mt-2 font-display text-2xl font-bold tracking-tight">
        <span className={danger ? "text-rose-400" : "text-white"}>{value}</span>
        {unit && (
          <span className="text-slate-500 text-base ml-1 font-mono">
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

function Panel({ children, title }) {
  return (
    <div className="bg-slate-900/60 border border-slate-800 rounded-sm p-4">
      <div className="font-display text-base font-semibold text-slate-50 mb-3">
        {title}
      </div>
      {children}
    </div>
  );
}
